// Active Recovery activities — restructured with indoor/outdoor and surface variants.

// `intervals` (optional) is a structured, unit-aware prescription rendered by
// <IntervalList>, which owns the full field list. Each activity uses its real
// metrics — run/walk/hike: distanceMi + paceMinPerMi; bike/elliptical/treadmill:
// distanceMi/paceMph + level/inclinePct; rowing: distanceM + splitSec + cadence
// (strokes); swimming: distanceM + paceSec100. Canonical units (mi/mph/ft)
// convert to metric on display; meters render as meters. No coded effort scales
// (RPE/Zone/watts) — `effort` is a plain word. `detail` prose stays as a
// fallback and degrades gracefully if intervals is absent.
const wo = (level, name, duration, detail, intervals) => ({ level, name, duration, detail, intervals });

export const recoveryActivities = [
  {
    id: 'walk-run',
    name: 'Walk / Run',
    icon: 'Footprints',
    image: '/generated/recovery-walk-run.png',
    gear: ['Walking or running shoes', 'Moisture-wicking apparel', 'Trail shoes for offroad', 'Optional weighted vest (5-20 lb)'],
    safety: ['Warm up 5 min before harder efforts', 'Run against traffic on roads', 'Stay hydrated', 'Tell someone your route'],
    form: 'Land mid-foot under hips, ~180 cadence at running pace, relaxed shoulders, slight forward lean. Walking: heel-to-toe, upright posture.',
    modes: {
      indoor: {
        label: 'Indoor',
        image: '/generated/recovery-walk-run.png',
        surfaces: [
          {
            id: 'treadmill', name: 'Treadmill', requiresEquipment: ['treadmill'],
            workouts: [
              wo('beginner', 'Walk + Light Jog Intervals', 20, '1 min jog at 5-6 mph / 2 min walk at 3.5 mph × 6. Easy.', [
                { repeat: 6, effort: 'Easy', steps: [
                  { durationMin: 1, label: 'Jog', paceMph: 5, paceMphMax: 6 },
                  { durationMin: 2, label: 'Walk', paceMph: 3.5 },
                ] },
              ]),
              wo('intermediate', 'Steady Aerobic', 35, '35 min @ 6-7 mph at 1% incline. Conversational pace.', [
                { durationMin: 35, label: 'Steady run', paceMph: 6, paceMphMax: 7, effort: 'Conversational', cue: '1% incline' },
              ]),
              wo('advanced', 'Tempo Run', 45, '10 min warm + 25 min @ 8-9 mph + 10 min cool. Push but sustainable.', [
                { durationMin: 10, label: 'Warm-up', effort: 'Easy' },
                { durationMin: 25, label: 'Tempo', paceMph: 8, paceMphMax: 9, effort: 'Push but sustainable' },
                { durationMin: 10, label: 'Cool-down', effort: 'Easy' },
              ]),
            ],
          },
          {
            id: 'indoor-track', name: 'Indoor Track', requiresEquipment: ['indoor-track'],
            workouts: [
              wo('beginner', 'Easy Laps', 25, '15 laps (~1.5 mi) at conversational pace. Walk-jog if needed.', [
                { distanceMi: 1.5, label: 'Easy laps', effort: 'Conversational', cue: '~15 laps · walk-jog if needed' },
              ]),
              wo('intermediate', 'Steady Mile Repeats', 35, '3 × 1 mile @ 5K pace with 2 min walk recovery between.', [
                { repeat: 3, steps: [
                  { distanceMi: 1, label: 'Mile', effort: '5K pace' },
                  { durationMin: 2, label: 'Walk recovery' },
                ] },
              ]),
              wo('advanced', 'Tabata Sprints', 30, '8 rounds: 20s all-out sprint / 10s walk × 8 sets. Brutal in 4 min blocks.', [
                { repeat: 8, cue: 'Tabata — 4 min block', steps: [
                  { durationSec: 20, label: 'Sprint', effort: 'All-out' },
                  { durationSec: 10, label: 'Walk' },
                ] },
              ]),
              wo('advanced', 'Distance Build', 50, '10 × 400m at 80% effort, 200m walk recovery. Negative split last 2.', [
                { repeat: 10, cue: 'Negative split last 2', steps: [
                  { label: '400 m', effort: '80% effort' },
                  { label: '200 m walk recovery' },
                ] },
              ]),
            ],
          },
        ],
      },
      outdoor: {
        label: 'Outdoor',
        image: '/generated/recovery-walk-run.png',
        surfaces: [
          {
            id: 'road', name: 'Road', requiresEquipment: [],
            workouts: [
              wo('beginner', 'Neighborhood Walk', 25, '1.5 mi flat at conversational pace. Bring water.', [
                { distanceMi: 1.5, label: 'Walk', effort: 'Conversational', cue: 'Flat · bring water' },
              ]),
              wo('intermediate', 'Steady Aerobic Run', 40, '4–5 mi at a conversational pace — you can still talk.', [
                { distanceMi: 4, distanceMiMax: 5, label: 'Run', effort: 'Conversational', cue: 'You can still talk' },
              ]),
              wo('advanced', 'Tempo + Cool', 60, '10 min easy + 30 min @ tempo + 10 min cool. Negative split.', [
                { durationMin: 10, label: 'Easy', effort: 'Easy' },
                { durationMin: 30, label: 'Tempo', effort: 'Tempo · negative split' },
                { durationMin: 10, label: 'Cool-down', effort: 'Easy' },
              ]),
            ],
          },
          {
            id: 'trail', name: 'Trail', requiresEquipment: [],
            workouts: [
              wo('beginner', 'Easy Trail Walk', 45, '2 mi rolling trail at an easy pace. Watch your footing.', [
                { distanceMi: 2, label: 'Trail walk', effort: 'Easy', cue: 'Rolling · watch footing' },
              ]),
              wo('intermediate', 'Rolling Trail Run', 50, '4-5 mi mixed terrain, 600-800 ft gain. Power-hike steep climbs.', [
                { distanceMi: 4, distanceMiMax: 5, label: 'Trail run', elevationFt: 600, elevationFtMax: 800, cue: 'Mixed terrain · power-hike steep climbs' },
              ]),
              wo('advanced', 'Long Trail Effort', 90, '8-10 mi with 1500 ft gain. Steady aerobic. Pack nutrition.', [
                { distanceMi: 8, distanceMiMax: 10, label: 'Trail run', elevationFt: 1500, effort: 'Steady aerobic', cue: 'Pack nutrition' },
              ]),
            ],
          },
        ],
        feedFilter: (a) => a.type === 'Run' || a.title?.toLowerCase().includes('walk'),
      },
    },
  },
  {
    id: 'cycling',
    name: 'Cycling',
    icon: 'Bike',
    image: '/generated/recovery-cycling.png',
    gear: ['Cycling shoes / clipless pedals (optional)', 'Padded shorts', 'Water bottles', 'Helmet (outdoor)'],
    safety: ['Adjust seat to hip height when standing next to bike', 'Knee slightly bent at bottom of pedal stroke', 'Helmet always outdoors'],
    form: 'Pedal in smooth circles, not just pushing down. Light grip on bars. Engage core, relax shoulders.',
    modes: {
      indoor: {
        label: 'Indoor',
        image: '/generated/recovery-cycling.png',
        surfaces: [
          {
            id: 'stationary', name: 'Stationary / Connected Bike', requiresEquipment: ['stationary-bike'],
            // Self-guided: connected platforms / built-in programs drive the session,
            // so the UI shows the self-guided card (no interval prescription). `detail`
            // prose is kept as loggable context / future-AI seed.
            guidance: 'self',
            selfGuidedNote: 'Hop into a class on your connected platform below, or pick a built-in program that matches today’s effort — then log it here.',
            workouts: [
              wo('beginner', 'Easy Spin', 25, '25 min at low resistance, smooth and steady legs.'),
              wo('intermediate', 'Endurance Ride', 45, '45 min at moderate resistance. Vary your pedalling speed.'),
              wo('advanced', 'Easy Aerobic + Climbs', 60, '60 min easy aerobic + 5 × 30s standing climbs every 10 min.'),
              wo('advanced', 'Interval Burner', 40, '5 × 4 min hard / 3 min easy recovery. Cool down 5 min.'),
            ],
          },
        ],
        connectedPlatforms: [
          { id: 'peloton', name: 'Peloton', tag: 'Live classes, leaderboards' },
          { id: 'zwift', name: 'Zwift', tag: 'Virtual rides, racing' },
          { id: 'wahoo', name: 'Wahoo SYSTM', tag: 'Structured training' },
          { id: 'trainerroad', name: 'TrainerRoad', tag: 'FTP-based plans' },
        ],
      },
      outdoor: {
        label: 'Outdoor',
        image: '/generated/recovery-cycling.png',
        surfaces: [
          {
            id: 'road-bike', name: 'Road', requiresEquipment: [],
            workouts: [
              wo('beginner', 'Easy Cruise', 30, '6-8 mi flat, easy gear. Stay relaxed.', [
                { distanceMi: 6, distanceMiMax: 8, label: 'Easy cruise', effort: 'Easy gear · relaxed', cue: 'Flat' },
              ]),
              wo('intermediate', 'Endurance Loop', 60, '15-20 mi rolling terrain. Conversational pace.', [
                { distanceMi: 15, distanceMiMax: 20, label: 'Endurance loop', effort: 'Conversational', cue: 'Rolling terrain' },
              ]),
              wo('advanced', 'Long Aerobic', 120, '35-45 mi steady. Practice fueling and pacing.', [
                { distanceMi: 35, distanceMiMax: 45, label: 'Long aerobic', effort: 'Steady', cue: 'Practice fueling and pacing' },
              ]),
            ],
          },
          {
            id: 'gravel-bike', name: 'Gravel / Trail', requiresEquipment: [],
            workouts: [
              wo('beginner', 'Mellow Gravel', 45, '8-10 mi flat gravel road. Focus on bike handling.', [
                { distanceMi: 8, distanceMiMax: 10, label: 'Mellow gravel', cue: 'Flat gravel road · focus on bike handling' },
              ]),
              wo('intermediate', 'Rolling Gravel', 90, '20-25 mi mixed surface, 800-1200 ft gain.', [
                { distanceMi: 20, distanceMiMax: 25, label: 'Rolling gravel', elevationFt: 800, elevationFtMax: 1200, cue: 'Mixed surface' },
              ]),
              wo('advanced', 'Big Day', 180, '40+ mi gravel with 2000+ ft. Bring extra nutrition.', [
                { distanceMi: 40, open: true, label: 'Big day', elevationFt: 2000, cue: '2000+ ft gain · bring extra nutrition' },
              ]),
            ],
          },
        ],
        feedFilter: (a) => a.type === 'Gravel Ride' || a.type === 'Road Ride',
      },
    },
  },
  {
    id: 'hiking',
    name: 'Hiking',
    icon: 'Mountain',
    image: '/generated/recovery-hiking.png',
    gear: ['Trail shoes or hiking boots', 'Daypack with water', 'Trekking poles (optional)', 'Layered clothing'],
    safety: ['Tell someone your route', 'Check weather', 'Carry a map / GPS', 'Stay on marked trails'],
    form: 'Shorten stride on inclines, plant whole foot. Engage core on descents.',
    workouts: [
      wo('beginner', 'Easy Trail Loop', 45, '2–3 mi with 200–400 ft of climbing, easy pace.', [
        { distanceMi: 2, distanceMiMax: 3, label: 'Hike', elevationFt: 200, elevationFtMax: 400, effort: 'Easy' },
      ]),
      wo('intermediate', 'Rolling Trail', 75, '4–5 mi with 800–1200 ft of climbing, moderate pace.', [
        { distanceMi: 4, distanceMiMax: 5, label: 'Hike', elevationFt: 800, elevationFtMax: 1200, effort: 'Moderate' },
      ]),
      wo('advanced', 'Summit Push', 120, '6-8 mi, 1500+ ft gain. Steady aerobic effort.', [
        { distanceMi: 6, distanceMiMax: 8, label: 'Summit push', elevationFt: 1500, open: true, effort: 'Steady aerobic' },
      ]),
    ],
  },
  {
    id: 'rowing',
    name: 'Rowing',
    icon: 'Anchor',
    image: '/generated/recovery-rowing.png',
    gear: ['Rowing machine (Concept2 ideal)', 'Heart rate monitor (optional)'],
    safety: ['Set damper 3-5 for recovery', 'Avoid hyperextension at finish', 'Keep back neutral'],
    form: 'Drive sequence: legs, back, arms. Recovery: arms, back, legs. Smooth and rhythmic.',
    workouts: [
      wo('beginner', 'Steady State', 15, '15 min at 20–22 strokes per minute. Smooth, easy effort.', [
        { durationMin: 15, label: 'Steady state', cadence: 20, cadenceMax: 22, cadenceUnit: 'strokes', effort: 'Easy', cue: 'Smooth' },
      ]),
      wo('intermediate', 'Aerobic Row', 30, '30 min at 22–24 strokes per minute. Negative split.', [
        { durationMin: 30, label: 'Aerobic row', cadence: 22, cadenceMax: 24, cadenceUnit: 'strokes', effort: 'Moderate', cue: 'Negative split' },
      ]),
      wo('advanced', 'Long Aerobic', 45, '45 min steady at 24–26 strokes per minute. Hold technique under fatigue.', [
        { durationMin: 45, label: 'Long aerobic', cadence: 24, cadenceMax: 26, cadenceUnit: 'strokes', effort: 'Steady', cue: 'Hold technique under fatigue' },
      ]),
    ],
  },
  {
    id: 'heavy-bag',
    name: 'Bag Work',
    icon: 'Flame',
    image: '/generated/martial-boxing.png',
    gear: ['Hand wraps (180 in)', 'Boxing gloves (12-16 oz)', 'Timer / interval app', 'Heavy bag / speed bag / double-end / slip bag'],
    safety: ['Always wrap before gloving up', 'Never punch with locked elbows', 'Snap punches back — don\'t push', 'Match glove weight to bag (heavier gloves for heavy bag, lighter for speed work)'],
    form: 'Stay loose, exhale on every strike, return hands to guard immediately, pivot on lead foot. Speed/double-end bag — rhythm over power; slip bag — head moves, hands stay up.',
    bagTypes: ['Heavy bag', 'Speed bag', 'Double-end bag', 'Slip bag', 'Maize bag', 'Aqua bag'],
    workouts: [
      wo('beginner', 'Technique Rounds (Heavy)', 18, '3 × 2 min light rounds, 1 min rest. Focus jab-cross.', [
        { repeat: 3, cue: 'Focus jab-cross', steps: [
          { durationMin: 2, label: 'Light round', effort: 'Light' },
          { durationMin: 1, label: 'Rest' },
        ] },
      ]),
      wo('beginner', 'Speed Bag Foundations', 15, '5 × 2 min speed bag, 1 min rest. Just keep rhythm — no power.', [
        { repeat: 5, cue: 'Rhythm over power', steps: [
          { durationMin: 2, label: 'Speed bag' },
          { durationMin: 1, label: 'Rest' },
        ] },
      ]),
      wo('intermediate', 'Combo Flow (Heavy)', 30, '6 × 3 min rounds. Add hooks, body shots. 60s rest.', [
        { repeat: 6, cue: 'Add hooks + body shots', steps: [
          { durationMin: 3, label: 'Round' },
          { durationSec: 60, label: 'Rest' },
        ] },
      ]),
      wo('intermediate', 'Double-End Reactive', 25, '5 × 3 min double-end, focus on hand return and slipping the rebound.', [
        { repeat: 5, cue: 'Hand return + slip the rebound', steps: [
          { durationMin: 3, label: 'Double-end round' },
        ] },
      ]),
      wo('advanced', 'Conditioning Bag', 45, '10 × 3 min rounds + movement. 30s burnout last 10s of each.', [
        { repeat: 10, cue: 'Burnout final 10s of each round', steps: [
          { durationMin: 3, label: 'Round + movement' },
        ] },
      ]),
      wo('advanced', 'Slip + Counter Circuit', 35, '8 × 3 min: slip bag 30s → heavy bag 90s → speed bag 60s. 30s rest between rounds.', [
        { repeat: 8, steps: [
          { durationSec: 30, label: 'Slip bag' },
          { durationSec: 90, label: 'Heavy bag' },
          { durationSec: 60, label: 'Speed bag' },
          { durationSec: 30, label: 'Rest' },
        ] },
      ]),
    ],
  },
  {
    id: 'elliptical-max',
    name: 'Elliptical / Max Trainer™',
    icon: 'Activity',
    image: '/generated/recovery-elliptical.png',
    // Self-guided: the machine's built-in programs (Hill, HIIT, auto-progression)
    // drive the session. Workouts below stay as data; UI shows the self-guided card.
    guidance: 'self',
    selfGuidedNote: 'Use your machine’s built-in programs (Hill, HIIT, auto-progression) — pick one that matches today’s effort, then log it here.',
    gear: ['Cross-training shoes', 'Water', 'Towel'],
    safety: ['Stand tall, avoid leaning on handles', 'Plant whole foot, drive through heel'],
    form: 'Upright posture, slight push-pull with arms, smooth elliptical motion.',
    workouts: [
      wo('beginner', 'Easy Glide (Elliptical)', 20, '20 min low resistance, moderate stride. Joint-friendly.'),
      wo('intermediate', 'Hill Profile (Elliptical)', 30, '30 min on rolling-hill program. Resistance 5-8.'),
      wo('advanced', 'Long Aerobic (Elliptical)', 45, '45 min steady. Add reverse stride every 5 min.'),
      wo('intermediate', 'Max Trainer™ HIIT', 14, '14 min interval program. Max effort on burst intervals.'),
      wo('advanced', 'Max Trainer™ Builder', 21, '21 min auto-progression. Push the rating bar to peak.'),
    ],
  },
  {
    id: 'jump-rope',
    name: 'Jump Rope',
    icon: 'Zap',
    image: '/generated/recovery-jump-rope.png',
    gear: ['Speed or weighted rope', 'Cushioned shoes', 'Cushioned surface (avoid concrete if possible)'],
    safety: ['Land softly on balls of feet', 'Don\'t jump too high — 1-2 inches is plenty', 'Stop if calves cramp'],
    form: 'Wrists do the work, not arms. Elbows close to body. Stay light on feet.',
    workouts: [
      wo('beginner', 'Foundations', 12, '5 × 1 min skips, 1 min rest. Basic bounce.', [
        { repeat: 5, cue: 'Basic bounce', steps: [
          { durationMin: 1, label: 'Skips' },
          { durationMin: 1, label: 'Rest' },
        ] },
      ]),
      wo('intermediate', 'Skill + Conditioning', 20, '10 × 1 min on / 30s rest. Mix in alternating foot.', [
        { repeat: 10, cue: 'Mix in alternating foot', steps: [
          { durationMin: 1, label: 'On' },
          { durationSec: 30, label: 'Rest' },
        ] },
      ]),
      wo('advanced', 'Boxer\'s Rounds', 30, '6 × 3 min rounds. Double-unders, criss-cross, footwork.', [
        { repeat: 6, cue: 'Double-unders, criss-cross, footwork', steps: [
          { durationMin: 3, label: 'Round' },
        ] },
      ]),
    ],
  },
  {
    id: 'swimming',
    name: 'Swimming',
    icon: 'Waves',
    image: '/generated/recovery-swimming.png',
    gear: ['Goggles', 'Swim cap', 'Kickboard / pull buoy (optional)'],
    safety: ['Never swim alone', 'Know pool depth before diving', 'Warm up 200m easy'],
    form: 'Long strokes, exhale underwater, rotate from hips, kick from hips not knees.',
    workouts: [
      wo('beginner', 'Easy Laps', 25, '10 × 50 m freestyle with 30s rest. Focus on form.', [
        { repeat: 10, cue: 'Focus on form', steps: [
          { distanceM: 50, label: 'Freestyle' },
          { durationSec: 30, label: 'Rest' },
        ] },
      ]),
      wo('intermediate', 'Mixed Set', 40, '400 m warm-up + 8 × 100 m + 200 m cool-down.', [
        { distanceM: 400, label: 'Warm-up', effort: 'Easy' },
        { repeat: 8, steps: [{ distanceM: 100, label: 'Freestyle' }] },
        { distanceM: 200, label: 'Cool-down', effort: 'Easy' },
      ]),
      wo('advanced', 'Long Distance', 50, '1500 m continuous + 4 × 100 m individual medley cool-down.', [
        { distanceM: 1500, label: 'Continuous', effort: 'Steady' },
        { repeat: 4, steps: [{ distanceM: 100, label: 'Individual medley' }] },
      ]),
    ],
  },
  {
    id: 'rock-climbing',
    name: 'Rock Climbing',
    icon: 'Mountain',
    image: '/generated/recovery-rock-climbing.png',
    gear: ['Climbing shoes', 'Chalk + chalk bag', 'Harness (rope climbing)', 'Belay device + locking carabiner', 'Helmet (outdoor lead / trad)'],
    safety: ['Check knot and belay before every climb', 'Communicate clearly with belayer', 'Warm up shoulders + fingers — no cold crimping', 'Build finger strength gradually — pulleys take months to heal'],
    form: 'Drive with legs, hang from straight arms when resting, hips close to wall, look for footholds before reaching, breathe through the moves.',
    modes: {
      indoor: {
        label: 'Indoor',
        image: '/generated/recovery-rock-climbing.png',
        surfaces: [
          {
            id: 'bouldering-gym', name: 'Bouldering Gym', requiresEquipment: [],
            workouts: [
              wo('beginner', 'Intro Sends', 45, 'V0–V2 problems. 6–8 sends, long rest between. Focus on footwork.'),
              wo('intermediate', 'Project + Volume', 75, '45 min volume on V2–V4 + 30 min projecting one V4–V5. Rest 3 min between burns.'),
              wo('advanced', 'Limit Bouldering', 90, '3 limit problems (V6+), 3–5 attempts each, full rest. Followed by 20 min easy down-climbing.'),
            ],
          },
          {
            id: 'top-rope-gym', name: 'Top-Rope / Auto-Belay', requiresEquipment: [],
            workouts: [
              wo('beginner', 'Foundations', 60, '4 routes 5.7–5.9. Practice resting on straight arms. Focus on smooth movement.'),
              wo('intermediate', 'Volume Pyramid', 75, '5.9 / 5.10a / 5.10b / 5.10a / 5.9 back-to-back with short rest. Build endurance.'),
              wo('advanced', 'Onsight Battery', 90, '6+ routes at 5.10c–5.11b onsight or flash. Walk between climbs.'),
            ],
          },
          {
            id: 'lead-gym', name: 'Indoor Lead', requiresEquipment: [],
            workouts: [
              wo('intermediate', 'Clipping Drill', 60, '3 easy lead routes, focus on clipping stance and reading the line.'),
              wo('advanced', 'Redpoint Project', 90, '2–3 burns on a project route, take falls intentionally above the bolt.'),
            ],
          },
        ],
      },
      outdoor: {
        label: 'Outdoor',
        image: '/generated/recovery-rock-climbing.png',
        surfaces: [
          {
            id: 'boulder-outdoor', name: 'Outdoor Bouldering', requiresEquipment: [],
            workouts: [
              wo('beginner', 'Crash Pad Session', 90, '4–6 V0–V2 problems on real rock. Long approach counts as warmup.'),
              wo('intermediate', 'Circuit Day', 120, '8–10 problems V2–V4 across multiple boulders. Spot for each other.'),
              wo('advanced', 'Project Day', 150, '1 hard line. Clean holds, brush, work the beta, send before the temps drop.'),
            ],
          },
          {
            id: 'sport-crag', name: 'Sport Crag', requiresEquipment: [],
            workouts: [
              wo('beginner', 'First Sport Day', 120, '3 single-pitch routes 5.8–5.10a. Practice clipping, lowering, cleaning.'),
              wo('intermediate', 'Pitches at Limit', 150, '4–6 routes 5.10–5.11. Mix onsights with redpoint burns.'),
              wo('advanced', 'Send Train', 180, '2–3 attempts on a project at limit grade. Rest fully, send between burns.'),
            ],
          },
          {
            id: 'trad-multi', name: 'Trad / Multi-Pitch', requiresEquipment: [],
            workouts: [
              wo('advanced', 'Multi-Pitch Day', 360, '4–6 pitches, route-finding, gear placements, anchors. Bring food + water + headlamp.'),
            ],
          },
        ],
        feedFilter: (a) => a.type === 'Climbing' || a.title?.toLowerCase().includes('climb'),
      },
    },
  },
];
